import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { Messages } from '~src/codegen/Localize';
import {SimulationOverviewSubspace} from '~src/simulation/components/SimulationOverviewSubspace';
import { ISimulationDetailInfo, ISimulationDetailWithUsageInfo, ISimulationInfo } from '~src/simulation/models/ISimulationInfo';
import { ISimulationActions } from '~src/simulation/serverApi/types/ISimulationActions';
import { addContentTypeToProjectHierarchy, populateContentBreadcrumbs, } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbHelpers';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '~src/spacesPlatform/components/spaceComponents/SpaceTelemetry';
import { Subspace, TabbedSpace } from '~src/spacesPlatform/components/spaceComponents/TabbedSpace';
import { INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';
import { ISpaceInfoTypeItemCount } from '~src/spacesPlatform/state/ContentCountsState';
import { SimulationSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';

import { ISanitizer } from '../../commonInfra/utils/Sanitize';
import { Simulations } from '../resources/Simulations';
import { SimulationSubspaceProps } from '../types/SimulationSubspaceProps';
import { SimulationSpaceHeader } from './SimulationSpaceHeader';

export type SimulationSpaceStateProps = Readonly<{
  contentCountsSimulation: ISpaceInfoTypeItemCount<'simulation'>;
  contentCountsUpToDate: boolean;
  isServerOrSiteAdmin: boolean;
  urls: SpacesSiteUrls;
  areUsersVisible: boolean;
}>;

export type SimulationSpaceDispatchProps = Readonly<{
  addToFavorites: () => void;
  enterSpace: (simulationDetailInfo?: ISimulationDetailWithUsageInfo) => void;
  fetchUpdatedContentCounts: () => void;
  markContentCountsUpToDate: () => void;
  removeFromFavorites: () => void;
}>;

export type SimulationSpaceOwnProps = Readonly<{
  simulationAllowedActions: ISimulationActions;
  simulationDetailInfo: ISimulationDetailWithUsageInfo;
  simulationInfo: ISimulationInfo;
  projectHierarchy: INamedItemInfo[];
  refreshData: () => void;

  // subplace info
  readonly subplaceName: string;
  readonly siteUrls: SiteUrls;

  readonly sanitizer: ISanitizer;
}>;

export type SimulationSpaceProps =
  SimulationSpaceStateProps
  & SimulationSpaceDispatchProps
  & SimulationSpaceOwnProps;

function overviewSubspace(subspaceProps: SimulationSubspaceProps): Subspace {

  return {
    name: 'overview',
    tabLabel: Messages.overview(),
    renderContent: () =>  <SimulationOverviewSubspace {...subspaceProps} />,
    shouldRenderTabContentCount: false,
  };
}

function getSimulationDetailWithUsage(
  simulationDetailInfo: ISimulationDetailInfo,
  simulationInfo: ISimulationInfo,
) {
  return {
    usageInfo: simulationInfo.usageInfo,
    usageInfoMaxValues: simulationInfo.usageInfoMaxValues,
    ...simulationDetailInfo,
  };
}

function getSimulationSubspaces(subspaceProps: SimulationSubspaceProps) {
  return _.compact([
    overviewSubspace(subspaceProps)
  ]);
}

export class SimulationSpace extends React.PureComponent<SimulationSpaceProps> {
  private memoizedSimulationDetailWithUsage = defaultMemoize(getSimulationDetailWithUsage);
  private memoizedGetSimulationSubspaces = defaultMemoize(getSimulationSubspaces);

  constructor(props: SimulationSpaceProps) {
    super(props);
    props.enterSpace();
  }

  public urlFromSubspaceName = (subspaceName?: SimulationSubspaceName) => {
    switch (subspaceName) {
      case 'overview':
        return this.props.urls.simulationOverviewUrl(this.props.simulationDetailInfo.id);
    }
  };

  public render() {
    const subspaceProps: SimulationSubspaceProps = {
      contentCountsSimulation: this.props.contentCountsSimulation,
      contentCountsUpToDate: this.props.contentCountsUpToDate,
      fetchUpdatedContentCounts: this.props.fetchUpdatedContentCounts,
      isServerOrSiteAdmin: this.props.isServerOrSiteAdmin,
      simulationAllowedActions: this.props.simulationAllowedActions,
      simulationDetailInfo: this.props.simulationDetailInfo,
      refreshData: this.props.refreshData,
      siteUrls: this.props.urls,
      areUsersVisible: this.props.areUsersVisible,
      sanitizer: this.props.sanitizer,
      enterSpace: this.props.enterSpace,
    };

    const subspaces = this.memoizedGetSimulationSubspaces(subspaceProps);

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.subplaceName as SimulationSubspaceName}
        urlForSubspace={this.urlFromSubspaceName}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'simulation'}
        spaceItemName={this.props.simulationDetailInfo.name}
      />
    );
  }

  public componentDidMount() {
	this.updateBreadcrumbs();
    maybeSendTelemetry_favoriteItemOpened(this.props.simulationDetailInfo);
    Simulations.markRecentlyViewed(this.props.simulationDetailInfo.id);
  }

  public componentDidUpdate(prevProps: SimulationSpaceProps) {
    if (this.props.simulationDetailInfo.id !== prevProps.simulationDetailInfo.id ||
      this.props.subplaceName !== prevProps.subplaceName) {
      this.props.enterSpace();
      this.updateBreadcrumbs();
    }
    if (this.props.simulationDetailInfo.id !== prevProps.simulationDetailInfo.id) {
      maybeSendTelemetry_favoriteItemOpened(this.props.simulationDetailInfo);
      Simulations.markRecentlyViewed(this.props.simulationDetailInfo.id);
    }
  }

  private updateBreadcrumbs() {
    const projectHierarchyWithType = addContentTypeToProjectHierarchy(this.props.projectHierarchy);
    populateContentBreadcrumbs([...projectHierarchyWithType, this.props.simulationDetailInfo], this.props.urls);
  }

  private mastheadElement(): React.ReactNode {
    const simulationDetailWithUsage = this.memoizedSimulationDetailWithUsage(
      this.props.simulationDetailInfo,
      this.props.simulationInfo,
    );

    return (
      <SimulationSpaceHeader
        itemInfo={simulationDetailWithUsage}
        availableActions={this.props.simulationAllowedActions}
        openDetail={() => null}
        addToFavorites={this.props.addToFavorites}
        removeFromFavorites={this.props.removeFromFavorites}
        urls={this.props.urls}
      />
    );
  }
}
